import { useState, useEffect } from 'react';
import { useUser, useUpdateUser, useRoles } from '@/hooks/useUsers';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { X, Eye, EyeOff } from 'lucide-react';

interface UserEditDialogProps {
  userId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UserEditDialog({ userId, open, onOpenChange }: UserEditDialogProps) {
  const { data: user, isLoading } = useUser(userId);
  const { data: rolesData } = useRoles();
  const updatePengguna = useUpdateUser();

  const [username, setPenggunaname] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [isActive, setIsAktif] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (user) {
      setPenggunaname(user.username || '');
      setEmail(user.email || '');
      setSelectedRoles(user.roles || []);
      setIsAktif(user.isActive ?? true);
      setPassword('');
    }
  }, [user]);

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!username.trim()) newErrors.username = 'Penggunaname is required';
    if (!email.trim()) newErrors.email = 'Email wajib diisi';
    if (password && password.length < 6) newErrors.password = 'Password must be at least 6 characters';
    if (selectedRoles.length === 0) newErrors.roles = 'Minimal satu peran wajib dipilih';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const data: any = {
      username,
      email,
      roles: selectedRoles,
      isActive,
    };
    if (password) {
      data.password = password;
    }

    try {
      await updatePengguna.mutateAsync({ id: userId, data });
      onOpenChange(false);
    } catch (error: any) {
      if (error.response?.data?.message) {
        setErrors({ submit: error.response.data.message });
      }
    }
  };

  const toggleRole = (role: string) => {
    setSelectedRoles((prev) =>
      prev.includes(role)
        ? prev.filter((r) => r !== role)
        : [...prev, role]
    );
  };

  const removeRole = (role: string) => {
    setSelectedRoles((prev) => prev.filter((r) => r !== role));
  };

  if (isLoading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <div className="py-8 text-center">Memuat...</div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Ubah Pengguna</DialogTitle>
          <DialogDescription>
            Perbarui informasi dan izin pengguna.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {errors.submit && (
            <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
              {errors.submit}
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="edit-username">Penggunaname</Label>
            <Input
              id="edit-username"
              value={username}
              onChange={(e) => setPenggunaname(e.target.value)}
              placeholder="Masukkan username"
            />
            {errors.username && (
              <p className="text-sm text-red-600">{errors.username}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-email">Email</Label>
            <Input
              id="edit-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Masukkan email"
            />
            {errors.email && (
              <p className="text-sm text-red-600">{errors.email}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-password">
              New Password <span className="text-muted-foreground">(biarkan kosong jika tidak diubah)</span>
            </Label>
            <div className="relative">
              <Input
                id="edit-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Masukkan kata sandi baru"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
            {errors.password && (
              <p className="text-sm text-red-600">{errors.password}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label>Roles (IAM)</Label>
            <Select onValueChange={(value) => toggleRole(value)}>
              <SelectTrigger>
                <SelectValue placeholder="Pilih peran" />
              </SelectTrigger>
              <SelectContent>
                {rolesData?.data.map((role) => (
                  <SelectItem key={role.value} value={role.value}>
                    <div className="flex flex-col">
                      <span>{role.label}</span>
                      <span className="text-xs text-muted-foreground">
                        {role.description}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.roles && (
              <p className="text-sm text-red-600">{errors.roles}</p>
            )}
            <div className="flex flex-wrap gap-2 mt-2">
              {selectedRoles.map((role) => {
                const roleInfo = rolesData?.data.find((r) => r.value === role);
                return (
                  <Badge key={role} variant="secondary" className="flex items-center gap-1">
                    {roleInfo?.label || role}
                    <button
                      type="button"
                      onClick={() => removeRole(role)}
                      className="ml-1 hover:text-red-600"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                );
              })}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="edit-isActive"
              checked={isActive}
              onCheckedChange={(checked) => setIsAktif(checked as boolean)}
            />
            <Label htmlFor="edit-isActive" className="text-sm font-normal">
              Pengguna aktif
            </Label>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Batal
            </Button>
            <Button type="submit" disabled={updatePengguna.isPending}>
              {updatePengguna.isPending ? 'Menyimpan...' : 'Simpan Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
